/**
 * One-time migration helper for renamed storage keys (Myelina Health rebrand).
 *
 * If `newKey` is unset but any `legacyKeys` entry has a value, copy the first
 * match to `newKey` and remove all legacy entries. Safe to call on every read;
 * subsequent calls are a no-op once the new key exists.
 *
 * Returns the resolved value at `newKey` after migration (or `null`).
 */
export function migrateStorageKey(
  storage: Storage | undefined | null,
  newKey: string,
  legacyKeys: string[],
): string | null {
  if (!storage) return null;
  try {
    const existing = storage.getItem(newKey);
    if (existing !== null) {
      // Best-effort clean up of any stale legacy entries so we don't drift.
      for (const k of legacyKeys) {
        if (storage.getItem(k) !== null) storage.removeItem(k);
      }
      return existing;
    }
    for (const k of legacyKeys) {
      const legacy = storage.getItem(k);
      if (legacy !== null) {
        storage.setItem(newKey, legacy);
        legacyKeys.forEach((old) => storage.removeItem(old));
        return legacy;
      }
    }
  } catch {
    /* storage unavailable / quota — silently ignore */
  }
  return null;
}
